import * as React from 'react'
import { Link } from 'react-router-dom'

import { Box, InfiniteScroll } from '@factorialco/deprecated-design-system'
import classNames from 'classnames/bind'
import range from 'lodash/range'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import UserAvatar from 'modules/core/components/UserAvatar'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'

import type { TeamViewEmployee } from 'modules/calendar/components/TeamView'
import type Store from 'modules/calendar/components/TeamView/components/Store'

import DayCell from './DayCell'
import styles from './index.module.css'

const cx = classNames.bind(styles)

interface TableBodyRowProps {
  store: Store
  employee: TeamViewEmployee
  employeeWorkingDays: string
}

const TableBodyRow = observer(function TableBodyRow({
  store,
  employee,
  employeeWorkingDays,
}: TableBodyRowProps) {
  const { navigation } = useNavigation()

  const { canManageTimeoff } = useCurrentSession()
  const isCurrent = employee.access.current
  const isSupervisedByCurrent = employee.supervisedByCurrent ?? false
  const canClick = isSupervisedByCurrent || isCurrent || canManageTimeoff

  const onEmployeeClick = (event: React.MouseEvent) => {
    if (!canClick) {
      event.preventDefault()
    }
  }

  const className = cx('link', {
    canClick,
  })

  return (
    <tr>
      <th className={styles.employeeCell}>
        <Link
          onClick={onEmployeeClick}
          to={navigation.employees.id.fullPath({ id: employee.id })}
          className={className}
        >
          <div className={styles.avatar}>
            <UserAvatar
              user={{ avatar: employee.access.avatar, fullName: employee.fullName }}
              size="s32"
            />
          </div>
          <div className={styles.userName}>
            <span>{employee.fullName}</span>
          </div>
        </Link>
      </th>
      {range(store.daysInMonth).map((day) => {
        const key = `${employee.id}-${day}`

        return (
          <DayCell
            employee={employee}
            store={store}
            day={day}
            key={key}
            employeeWorkingDays={employeeWorkingDays}
          />
        )
      })}
    </tr>
  )
})

interface Props {
  store: Store
  employees: TeamViewEmployee[]
  employeesWorkingDays: Record<string, string>
  scrollListener: () => void
  paginationInfo: { hasMore?: boolean; [key: string]: unknown } | null | undefined
  scrollWrapper: HTMLDivElement | null
  isSearching?: boolean
}

const TableBody = observer(function TableBody({
  store,
  employees,
  employeesWorkingDays,
  scrollListener,
  paginationInfo,
  scrollWrapper,
  isSearching = false,
}: Props) {
  const hasMore =
    !isSearching && paginationInfo && 'hasMore' in paginationInfo ? paginationInfo.hasMore : false
  const cursor = paginationInfo && 'cursor' in paginationInfo ? paginationInfo.cursor : null

  return (
    <InfiniteScroll
      element="tbody"
      pageStart={1}
      loadMore={scrollListener}
      hasMore={hasMore}
      initialLoad={false}
      getScrollParent={() => scrollWrapper}
      useWindow={false}
      loader={
        <tr key={`infiniteLoader-${cursor}`}>
          <td colSpan={store.daysInMonth + 1}>
            <Box flexDirection="row" justifyContent="center" alignItems="center" padding="s8">
              <Loading />
            </Box>
          </td>
        </tr>
      }
    >
      {employees.map((employee) => (
        <TableBodyRow
          store={store}
          employee={employee}
          key={employee.id}
          employeeWorkingDays={employeesWorkingDays[String(employee.id)] ?? ''}
        />
      ))}
    </InfiniteScroll>
  )
})

export default TableBody
